import React, { useState, useRef, useEffect } from "react";
import { Settings } from "lucide-react";

export const ALL_AUTO_EVENTS = [
  // Navigation
  { name: "page_view",           label: "Page View",             category: "Navigation" },
  { name: "page_scroll",         label: "Page Scroll",           category: "Navigation" },
  // Interactions
  { name: "element_click",       label: "Element Click",         category: "Interactions" },
  { name: "element_hover",       label: "Element Hover",         category: "Interactions" },
  { name: "dead_click",          label: "Dead Click",            category: "Interactions" },
  { name: "rage_click",          label: "Rage Click",            category: "Interactions" },
  { name: "text_selection",      label: "Text Selection",        category: "Interactions" },
  { name: "element_view",        label: "Element View",          category: "Interactions" },
  { name: "copy_action",         label: "Copy Action",           category: "Interactions" },
  { name: "context_menu",        label: "Context Menu",          category: "Interactions" },
  // Forms
  { name: "form_submit",         label: "Form Submit",           category: "Forms" },
  { name: "form_validation_error", label: "Form Validation Error", category: "Forms" },
  { name: "form_abandoned",      label: "Form Abandoned",        category: "Forms" },
  { name: "form_focus",          label: "Form Focus",            category: "Forms" },
  // Media
  { name: "media_play",          label: "Media Play",            category: "Media" },
  { name: "media_pause",         label: "Media Pause",           category: "Media" },
  { name: "media_ended",         label: "Media Ended",           category: "Media" },
  // Session
  { name: "session_start",       label: "Session Start",         category: "Session" },
  { name: "session_end",         label: "Session End",           category: "Session" },
  { name: "exit_intent",         label: "Exit Intent",           category: "Session" },
  // Errors
  { name: "js_error",            label: "JS Error",              category: "Errors" },
  { name: "network_error",       label: "Network Error",         category: "Errors" },
  // Performance
  { name: "page_performance",    label: "Page Performance",      category: "Performance" },
  { name: "page_summary",        label: "Page Summary",          category: "Performance" },
];

const ALL_AUTO_EVENT_NAMES = ALL_AUTO_EVENTS.map((e) => e.name);

// Group events by category preserving insertion order
const CATEGORIES = ALL_AUTO_EVENTS.reduce((acc, ev) => {
  if (!acc[ev.category]) acc[ev.category] = [];
  acc[ev.category].push(ev);
  return acc;
}, {});

/**
 * Gear button + popup that lets the user toggle which auto-captured events
 * appear in flow intermediate steps.
 *
 * Props:
 *   excludedAutoEventNames  string[]  — names currently excluded (empty = show all)
 *   onExcludedChange        fn        — called with new excluded array
 *   theme                   object    — trodoAnalyticsTheme
 */
export default function FlowAutoEventSettings({ excludedAutoEventNames = [], onExcludedChange, theme = {} }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const excludedSet = new Set(excludedAutoEventNames);
  const useAutoEvents = excludedSet.size < ALL_AUTO_EVENT_NAMES.length;
  const allSelected = excludedSet.size === 0;
  const noneSelected = excludedSet.size === ALL_AUTO_EVENT_NAMES.length;
  const someSelected = !allSelected && !noneSelected;

  const handleMasterToggle = () => {
    onExcludedChange(useAutoEvents ? ALL_AUTO_EVENT_NAMES : []);
  };

  const handleAllToggle = () => {
    onExcludedChange(allSelected ? ALL_AUTO_EVENT_NAMES : []);
  };

  const handleIndividual = (name) => {
    const next = new Set(excludedSet);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onExcludedChange([...next]);
  };

  const border = theme.border || "var(--border-default)";
  const accent = theme.primaryLight;
  const muted  = theme.textMuted  || "var(--text-secondary)";

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors"
        style={{
          borderColor: border,
          backgroundColor: open ? "var(--surface-sunken)" : "var(--surface-base)",
          color: muted,
        }}
        title="Auto-event filter"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Configure auto-event visibility in flow steps"
      >
        <Settings className="h-4 w-4" strokeWidth={2} />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-[120] mt-1 rounded-lg border shadow-lg"
          style={{
            backgroundColor: "var(--surface-base)",
            borderColor: border,
            width: "min(100vw - 2rem, 17rem)",
          }}
          role="dialog"
          aria-label="Auto-event filter options"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Master toggle */}
          <div className="px-3 pt-2.5 pb-1.5 border-b" style={{ borderColor: border }}>
            <label className="flex cursor-pointer items-center gap-2 text-[11px] font-semibold text-ink">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-line-strong"
                style={{ accentColor: accent }}
                checked={useAutoEvents}
                onChange={handleMasterToggle}
              />
              Use Auto Events
            </label>
          </div>

          {/* Scrollable event list */}
          <div
            className="overflow-y-auto px-3 py-2"
            style={{
              maxHeight: 300,
              opacity: useAutoEvents ? 1 : 0.4,
              pointerEvents: useAutoEvents ? "auto" : "none",
            }}
          >
            {/* "All" select-all row */}
            <label className="flex cursor-pointer items-center gap-2 text-[11px] font-medium text-ink-secondary mb-1.5">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-line-strong"
                style={{ accentColor: accent }}
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={handleAllToggle}
              />
              All
            </label>

            {/* Events grouped by category */}
            {Object.entries(CATEGORIES).map(([cat, events]) => (
              <div key={cat} className="mt-2">
                <div
                  className="text-[10px] font-semibold uppercase tracking-wide mb-1"
                  style={{ color: muted }}
                >
                  {cat}
                </div>
                <ul className="space-y-1">
                  {events.map((ev) => (
                    <li key={ev.name}>
                      <label className="flex cursor-pointer items-center gap-2 text-[11px] text-ink-secondary">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-line-strong"
                          style={{ accentColor: accent }}
                          checked={!excludedSet.has(ev.name)}
                          onChange={() => handleIndividual(ev.name)}
                        />
                        {ev.label}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
